import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import '../screens/ConversationsScreen.css';

interface Conversation {
  id: string;
  topic_italian: string;
  topic_bengali: string;
  topic_english: string;
  description_italian?: string;
  description_bengali?: string;
  description_english?: string;
  category?: string;
  icon?: string;
  order_index: number;
}

const ConversationsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const content = {
    bn: {
      title: "কথোপকথন",
      searchPlaceholder: "বিষয় খুঁজুন...",
      back: "ফিরে যান",
      noResults: "কোন ফলাফল পাওয়া যায়নি",
      loading: "লোড হচ্ছে..."
    },
    it: {
      title: "Conversazioni",
      searchPlaceholder: "Cerca argomento...",
      back: "Indietro",
      noResults: "Nessun risultato trovato",
      loading: "Caricamento..."
    },
    en: {
      title: "Conversations",
      searchPlaceholder: "Search topic...",
      back: "Back",
      noResults: "No results found",
      loading: "Loading..."
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    filterConversations();
  }, [searchQuery, conversations, currentLanguage]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      
      const { data: conversationsData, error: conversationsError } = await supabase
        .from('conversations')
        .select('*')
        .order('order_index', { ascending: true });

      if (conversationsError) {
        console.error('Error loading conversations:', conversationsError);
        setLoading(false);
        return;
      }

      if (conversationsData) {
        setConversations(conversationsData);
        setFilteredConversations(conversationsData);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterConversations = () => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = conversations.filter(conv => {
      const topicIt = conv.topic_italian?.toLowerCase() || '';
      const topicBn = conv.topic_bengali?.toLowerCase() || '';
      const topicEn = conv.topic_english?.toLowerCase() || '';
      const category = conv.category?.toLowerCase() || '';
      
      return topicIt.includes(query) || 
             topicBn.includes(query) || 
             topicEn.includes(query) ||
             category.includes(query);
    });

    setFilteredConversations(filtered);
  };

  const getTopicName = (conversation: Conversation) => {
    if (currentLanguage === 'it') return conversation.topic_italian;
    if (currentLanguage === 'bn') return conversation.topic_bengali;
    return conversation.topic_english;
  };

  const getTopicNameSecondary = (conversation: Conversation) => {
    // Show Italian and Bengali in bilingual format like WordsScreen
    if (currentLanguage === 'it') return conversation.topic_bengali;
    if (currentLanguage === 'bn') return conversation.topic_italian;
    return conversation.topic_italian; // For English, show Italian as secondary
  };

  const getCardColorClass = (index: number) => {
    const colors = ['grammar', 'mastery', 'practice', 'word-meaning', 'opposite-words', 'words'];
    return colors[index % colors.length];
  };

  return (
    <div className="conversations-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content[currentLanguage].title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Search Bar */}
      <div className="conversations-search">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder={content[currentLanguage].searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Featured Cards - Like WordsScreen */}
      <div className="featured-section">
        {loading ? (
          <div className="loading-message">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <p>{content[currentLanguage].loading}</p>
            </div>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="no-results">{content[currentLanguage].noResults}</div>
        ) : (
          <div className="featured-cards">
            {filteredConversations.map((conversation, index) => (
              <div
                key={conversation.id}
                className={`featured-card ${getCardColorClass(index)}`}
                onClick={() => navigate(`/conversations/${conversation.id}`)}
              >
                <div className="card-header">
                  <span className="card-title">
                    <span className="card-title-line">{getTopicName(conversation)}</span>
                    <span className="card-title-line">{getTopicNameSecondary(conversation)}</span>
                  </span>
                  <span className="heart-icon">♡</span>
                </div>
                <div className="card-icon">{conversation.icon || '💬'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ConversationsScreen;
